import { motion } from "framer-motion";
import { Sparkles, MapPin, Ruler, Star, Flame } from "lucide-react";
import { formatMXN, buildWhatsAppUrl } from "../lib/api";

export default function PreSaleSection({ properties = [], onSelect, content = {} }) {
  const presale = properties.filter((p) => p.is_presale).slice(0, 2);
  if (presale.length === 0) return null;

  return (
    <section
      id="preventa"
      data-testid="presale-section"
      className="relative py-28 overflow-hidden bg-[#0B1A0F]"
    >
      {/* Magical golden background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#15301E] via-[#0B1A0F] to-[#15301E]" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(241,195,107,0.55), transparent 70%), radial-gradient(ellipse 60% 50% at 50% 100%, rgba(184,224,122,0.35), transparent 70%)",
          }}
        />
        <div className="fog-layer" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="promo-ribbon inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.25em] font-bold shadow-lg">
            <Flame className="w-4 h-4" /> {content.presale_subtitle || "Sólo 2 lotes en preventa"}
          </span>
          <h2 className="mt-6 font-serif text-5xl sm:text-6xl text-[#F2EFE6] leading-[1.05]">
            {content.presale_title || "Preventa $1,000 / m²"}
          </h2>
          <p className="mt-5 text-[#F2EFE6]/75 text-lg">
            {content.presale_description ||
              "Dos lotes seleccionados a precio de fundación. Cuando se vayan, este precio no volverá."}
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 gap-8" style={{ perspective: "1400px" }}>
          {presale.map((p, idx) => (
            <motion.button
              type="button"
              key={p.id}
              data-testid={`presale-card-${idx}`}
              onClick={() => onSelect?.(p)}
              initial={{ opacity: 0, y: 60, rotateX: 12 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              whileHover={{ y: -10, rotateY: idx === 0 ? -3 : 3, rotateX: -3, scale: 1.02 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: idx * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group relative text-left rounded-[2rem] overflow-hidden border border-[#F1C36B]/40 bg-gradient-to-br from-[#15301E] to-[#0B1A0F] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]"
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Shimmering border */}
              <div className="absolute -inset-px rounded-[2rem] bg-gradient-to-r from-[#F1C36B] via-[#FFF7C2] to-[#B8E07A] opacity-40 group-hover:opacity-80 transition-opacity blur-sm pointer-events-none" />
              <div className="relative h-72 overflow-hidden">
                <motion.img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 1.2 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F] via-transparent to-transparent" />
                {/* Promo badge */}
                <div className="absolute top-4 left-4 promo-ribbon px-4 py-1.5 rounded-full text-[11px] font-bold shadow-md">
                  PREVENTA · LOTE #{p.lot_number}
                </div>
                <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-[#0B1A0F]/70 backdrop-blur-md px-3 py-1.5 rounded-full text-xs text-[#F1C36B] border border-[#F1C36B]/30">
                  <Star className="w-3.5 h-3.5 fill-[#F1C36B]" /> Edición fundadores
                </div>
              </div>

              <div className="relative p-7">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#F1C36B]">
                  <MapPin className="w-3 h-3" /> {p.location}
                </div>
                <h3 className="mt-2 font-serif text-3xl text-[#F2EFE6] group-hover:text-[#F1C36B] transition-colors">
                  {p.name}
                </h3>

                <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-xl bg-[#0B1A0F]/60 border border-[#B8E07A]/15 py-3 px-2">
                    <div className="text-[9px] uppercase tracking-wider text-[#F2EFE6]/55">Superficie</div>
                    <div className="mt-0.5 font-serif text-xl text-[#B8E07A]">{p.surface.toLocaleString()} m²</div>
                  </div>
                  <div className="rounded-xl bg-[#F1C36B]/15 border border-[#F1C36B]/30 py-3 px-2">
                    <div className="text-[9px] uppercase tracking-wider text-[#F1C36B]">Por m²</div>
                    <div className="mt-0.5 font-serif text-xl text-[#F1C36B]">${p.price_per_m2.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl bg-[#0B1A0F]/60 border border-[#B8E07A]/15 py-3 px-2">
                    <div className="text-[9px] uppercase tracking-wider text-[#F2EFE6]/55">Total</div>
                    <div className="mt-0.5 font-serif text-xl text-[#F2EFE6]">{formatMXN(p.price)}</div>
                  </div>
                </div>

                <ul className="mt-5 space-y-2">
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#F2EFE6]/80">
                      <Sparkles className="w-3.5 h-3.5 text-[#F1C36B] mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-wrap gap-3">
                  <span className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F1C36B] to-[#B8E07A] text-[#0B1A0F] px-5 py-2.5 text-sm font-bold">
                    Apartar este lote
                  </span>
                  <a
                    href={buildWhatsAppUrl(
                      `Hola, me interesa la PREVENTA del lote "${p.name}" (${p.surface} m² × $${p.price_per_m2}/m²) en Pextlán. ¿Sigue disponible?`
                    )}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#F2EFE6]/30 text-[#F2EFE6] hover:bg-[#F2EFE6]/10 px-4 py-2.5 text-xs font-semibold"
                    data-testid={`presale-wa-${idx}`}
                  >
                    Por WhatsApp
                  </a>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-[#F2EFE6]/60 italic">
            <Ruler className="w-4 h-4 inline mr-1 text-[#F1C36B]" />
            Precio de preventa válido sólo para los primeros 2 lotes apartados — los 12 restantes parten desde $1,500/m².
          </p>
        </div>
      </div>
    </section>
  );
}
